import type { parseEnv } from './env';
import { FeatureFlag, type ICockpitConfig, type ICockpitRuntimeConfig, type IPublicCockpitConfig } from './types';

export type ICockpitConfigSource = ReturnType<typeof parseEnv>;

const publicConfigKeys = [
    'SERVICE_NAME',
    'ENVIRONMENT_NAME',
    'S_BASECAMP_URL',
    'S_EXTERNAL_API_URL',
    'S_HERE_CACHE_URL',
    'S_RAAS_URL',
    'S_BASECAMP_MAINT_URL',
    'S_BASECAMP_PERSISTER_URL',
    'S_SOURCING_STORAGE_URL',
    'S_DISCOVERER_URL',
    'S_SALES_PORTAL_URL',
    'S_SALES_PORTAL_API_URL',
    'S_LISTING_PERSISTER_URL',
    'S_PROPX_URL',
    'S_PIPELINE_AUDIT_PERSISTER_URL',
    'S_RAM_URL',
    'S_THEMERGER_API_URL',
    'S_CALCENGINE_URL',
    'HOUSEHOLD_VERSION',
    'LOOKER_RENT_ROLL_DASHBOARD_URL',
    'DEFAULT_ROUTE',
    'EMAIL_DOCUMENT_SENDING_ADDRESSES',
    'IMAGE_RESIZER_MAX_SIZE',
    'ID_COMPANY_PEAKSIDE_FRA',
    'SENTRY_ENABLED',
    'SENTRY_DEBUG',
    'SENTRY_DSN',
    'LAUNCH_NOTES_ENABLED',
    'LAUNCH_NOTES_PROJECT',
    'GTM_ID',
    'GTM_ENABLED',
    'POSTHOG_HOST',
    'POSTHOG_DASHBOARD_URL',
    'FBASE_API_KEY',
    'FBASE_AUTH_DOMAIN',
    'FBASE_PROJECT_ID',
    'DEBUG_IS_EXTERNAL_USER',
    'JOBS_PROVIDER',
    'JOBS_POLLING_INTERVAL_MS',
    'S_ELECTRIC_URL',
    'UNLEASH_API_URL',
] as const satisfies readonly (keyof IPublicCockpitConfig)[];

const authenticatedOnlyConfigKeys = [
    'THEMERGER_API_KEY',
    'HERE_CACHE_KEY',
    'LAUNCH_NOTES_TOKEN',
    'GOOGLE_MAPS_TILES_API_KEY',
    'GOOGLE_MAPS_API_KEY',
    'POSTHOG_KEY',
    'ELECTRIC_USERNAME',
    'ELECTRIC_PASSWORD',
    'UNLEASH_API_TOKEN',
] as const satisfies readonly (keyof ICockpitConfig)[];

const publicFeatureFlags = [FeatureFlag.F_USE_EMAIL_PASS_AUTH] as const;

const pickConfigFields = <TConfig extends object, TKey extends keyof TConfig>(config: TConfig, keys: readonly TKey[]): Pick<TConfig, TKey> => {
    return keys.reduce(
        (acc, key) => {
            acc[key] = config[key];
            return acc;
        },
        {} as Pick<TConfig, TKey>,
    );
};

export const buildPublicCockpitConfig = (config: ICockpitConfigSource): IPublicCockpitConfig => {
    return {
        IS_AUTHENTICATED_CONFIG: false,
        ...pickConfigFields(config, publicConfigKeys),
        ...pickConfigFields(config, publicFeatureFlags),
    };
};

export const buildAuthenticatedCockpitConfig = (config: ICockpitConfigSource): ICockpitConfig => {
    return {
        ...buildPublicCockpitConfig(config),
        IS_AUTHENTICATED_CONFIG: true,
        ...pickConfigFields(config, authenticatedOnlyConfigKeys),
        ...pickConfigFields(config, Object.values(FeatureFlag)),
    };
};

export const isAuthenticatedCockpitConfig = (config: ICockpitRuntimeConfig | undefined): config is ICockpitConfig => {
    return config?.IS_AUTHENTICATED_CONFIG === true;
};
